import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Form, Input, Spin, notification } from "antd";
import { Link, useNavigate } from "react-router-dom";
import authService from "services/authService";
import RouteEnum from "constants/RouteEnum";
import { useTranslation } from "react-i18next";

const passwordRules = [
  { required: true, message: "Password is required" },
  {
    pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{12,}$/,
    message: "Use 12+ characters with upper, lower, number and symbol.",
  },
];

const AcceptInvitation = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const rawToken = params.get("token") || "";
    window.history.replaceState({}, document.title, window.location.pathname);
    setToken(rawToken);
    if (!rawToken) {
      setError(t("invalidInvitation"));
      setLoading(false);
      return;
    }
    authService
      .inspectInvitation(rawToken)
      .then(setInvitation)
      .catch(() => setError(t("invalidInvitation")))
      .finally(() => setLoading(false));
  }, [t]);

  const submit = async (values) => {
    setLoading(true);
    try {
      await authService.acceptInvitation({
        token,
        password: values.password,
        user_name: values.user_name,
      });
      notification.success({ message: t("invitationAccepted") });
      navigate(RouteEnum.Login, { replace: true });
    } catch {
      notification.error({ message: t("genericError") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-layout d-flex align-items-center justify-content-center">
      <Card title={t("acceptInvitation")} style={{ width: 480, maxWidth: "94%" }}>
        <Spin spinning={loading}>
          {error && <Alert type="error" message={error} showIcon />}
          {invitation && (
            <Form layout="vertical" onFinish={submit}>
              <Form.Item label={t("email")}>
                <Input value={invitation.email_hint} disabled />
              </Form.Item>
              <Form.Item
                name="user_name"
                label={t("name")}
                initialValue={invitation.user_name}
                rules={[{ required: true }]}
              >
                <Input autoComplete="name" />
              </Form.Item>
              <Form.Item name="password" label={t("password")} rules={passwordRules}>
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Form.Item
                name="confirm"
                label={t("confirmPassword")}
                dependencies={["password"]}
                rules={[
                  { required: true },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      return !value || getFieldValue("password") === value
                        ? Promise.resolve()
                        : Promise.reject(new Error(t("passwordsDoNotMatch")));
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Button htmlType="submit" type="primary" block disabled={loading}>
                {t("acceptInvitation")}
              </Button>
            </Form>
          )}
          <div className="mt-3">
            <Link to={RouteEnum.Login}>{t("backToLogin")}</Link>
          </div>
        </Spin>
      </Card>
    </div>
  );
};

export default AcceptInvitation;
